/**
 * "Your package changed" — the central control's push to every open screen.
 *
 * The plan asks that a change the Super Admin makes reaches the staff screens
 * «في نفس اللحظة». The server pushes one line over the live socket
 * (Backend/lib/liveSocket.js, `?control=1`) to every connected screen of the
 * institution that changed, and this hook calls `onChange`, which re-reads the
 * capabilities. The token goes in the first frame, never in the URL, exactly as
 * the staff queue board does it.
 *
 * When a proxy will not upgrade, the socket keeps retrying with a growing
 * pause, and a slow poll covers the gap: the change still arrives, a minute or
 * two later instead of at once.
 */

import { useEffect, useRef } from 'react';
import { API_BASE_URL, tokenStore } from '../api/client';

const BACKOFF_MS = [1000, 3000, 8000, 20000, 45000];
const POLL_MS = 90 * 1000;

function socketUrl() {
  const base = /^https?:/.test(API_BASE_URL)
    ? API_BASE_URL.replace(/^http/, 'ws')
    : `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}${API_BASE_URL}`;
  return `${base}/live/ws?control=1`;
}

/**
 * @param {boolean}  enabled   only for a session that belongs to an institution
 * @param {Function} onChange  called when the package changed (and on the slow poll)
 */
export function useControlChannel(enabled, onChange) {
  const latest = useRef(onChange);
  latest.current = onChange;

  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || typeof WebSocket === 'undefined') return undefined;
    let stopped = false;
    let ws = null;
    let retry = null;
    let failures = 0;

    const connect = () => {
      if (stopped) return;
      try {
        ws = new WebSocket(socketUrl());
      } catch {
        schedule();
        return;
      }
      ws.onopen = () => {
        try {
          ws.send(JSON.stringify({ auth: tokenStore.get() }));
        } catch {
          /* closing */
        }
      };
      ws.onmessage = (event) => {
        let message = null;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }
        if (message && message.type === 'control-ready') failures = 0;
        if (message && message.type === 'control') latest.current();
      };
      ws.onclose = () => {
        ws = null;
        schedule();
      };
      ws.onerror = () => {
        try {
          ws && ws.close();
        } catch {
          /* already closed */
        }
      };
    };

    const schedule = () => {
      if (stopped) return;
      const wait = BACKOFF_MS[Math.min(failures, BACKOFF_MS.length - 1)];
      failures += 1;
      retry = setTimeout(connect, wait);
    };

    connect();
    const poll = setInterval(() => latest.current(), POLL_MS);

    return () => {
      stopped = true;
      clearTimeout(retry);
      clearInterval(poll);
      if (ws) {
        ws.onclose = null;
        try {
          ws.close();
        } catch {
          /* already closed */
        }
      }
    };
  }, [enabled]);
}

export default useControlChannel;
